/**
 * Home: the visitor landing (code + demos + sign in) until Firebase Auth
 * reports a user, then the signed-in Home (HomeApp).
 */
import { lazy, Suspense, useEffect, useState } from "react";
import type { ProjectSource } from "../data/loadProject";
import { watchAuth, type AuthUser } from "../data/auth";
import { Landing } from "./Landing";
import { HomeApp } from "./HomeApp";
import { StudentView } from "./Classrooms";
import { HelpView } from "./HelpView";
import { SubscriptionView } from "./SubscriptionView";
import { useRoute } from "./router";
import { uiDir } from "../player/rtl";
import { currentLanguage } from "../player/i18n";
import { PlayHome } from "./PlayHome";
import { IS_PLAY_SITE } from "../site";

const Editor = lazy(() => import("../editor/Editor"));

export function Home({ onPlay, error }: { onPlay: (source: ProjectSource) => void; error?: string }) {
  return IS_PLAY_SITE ? <PlayOnlyHome onPlay={onPlay} error={error} /> : <FullHome onPlay={onPlay} error={error} />;
}

/** play.makeit.app: the code box and demos, and classroom students (no account, no editor). */
function PlayOnlyHome({ onPlay, error }: { onPlay: (source: ProjectSource) => void; error?: string }) {
  const route = useRoute();
  if (route.name === "student") {
    return (
      <div className="happ student-only" dir={uiDir(currentLanguage())}>
        <main className="happ-main">
          <StudentView onPlay={onPlay} />
        </main>
      </div>
    );
  }
  return <PlayHome onPlay={onPlay} error={error} />;
}

function FullHome({ onPlay, error }: { onPlay: (source: ProjectSource) => void; error?: string }) {
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined); // undefined = not known yet
  useEffect(() => {
    let off: (() => void) | undefined;
    let gone = false;
    void watchAuth((u) => {
      if (!gone) setUser(u);
    }).then((f) => (gone ? f() : (off = f)));
    return () => {
      gone = true;
      off?.();
    };
  }, []);
  const route = useRoute();
  if (import.meta.env.DEV && route.name === "edit" && (route.id.startsWith("local-test") || route.id.startsWith("local-demo"))) {
    // #/edit/local-demo~<lang>~<id>: a bundled demo in the editor (screenshots for the landing page)
    const demo = route.id.startsWith("local-demo~") ? (`demo:${route.id.split("~").slice(1).join("/")}` as const) : null;
    return (
      <Suspense fallback={<div className="screen" />}>
        <Editor key={route.id} projectId={route.id} uid="" onPlay={onPlay} testMode={demo ?? (route.id === "local-test-game" ? "game" : "quiz")} />
      </Suspense>
    );
  }
  if (import.meta.env.DEV && route.name === "subscription" && !user && location.search.includes("devsub")) {
    // ?devsub[=premium|web|school|pack]#/subscription: the Subscription page without an account (layout checks)
    const mode = new URLSearchParams(location.search).get("devsub") ?? "";
    const expiresAt = Date.now() + 30 * 864e5;
    const pack = mode === "pack" ? { id: "purchase-dev", name: "", role: "manager" as const, expiresAt, managerEmail: "dev@example.com" } : null;
    const sub = mode ? { active: true, source: mode === "school" ? ("legacy" as const) : ("revenuecat" as const), entitlement: pack ? "schools" : "teachers", productId: "x", store: mode === "web" || pack ? "rc_billing" : mode === "school" ? "legacy" : "app_store", expiresAt, willRenew: mode !== "school", trial: mode === "web", sandbox: false, managementUrl: null, schoolAdminEmail: mode === "school" ? "director@escola.cat" : null, school: pack, checkedAt: Date.now() } : null;
    return (
      <div className="happ student-only" dir={uiDir(currentLanguage())}>
        <main className="happ-main">
          <SubscriptionView user={{ uid: "dev", email: "dev@example.com", displayName: "Dev", hasPassword: true, hasApple: false }} account={{ email: "", serverId: 0, folders: [], subscription: sub }} />
        </main>
      </div>
    );
  }
  if (user === undefined) return <div className="screen" />;
  if (user && route.name === "edit") {
    return (
      <Suspense fallback={<div className="screen" />}>
        <Editor key={route.id} projectId={route.id} uid={user.uid} onPlay={onPlay} />
      </Suspense>
    );
  }
  if (user) return <HomeApp user={user} onPlay={onPlay} />;
  // A classroom student needs no account; neither does the help.
  if (route.name === "help") {
    return (
      <div className="happ student-only" dir={uiDir(currentLanguage())}>
        <main className="happ-main">
          <a className="back" href="#/">
            ‹ Make It
          </a>
          <HelpView />
        </main>
      </div>
    );
  }
  if (route.name === "student") {
    return (
      <div className="happ student-only" dir={uiDir(currentLanguage())}>
        <main className="happ-main">
          <StudentView onPlay={onPlay} />
        </main>
      </div>
    );
  }
  return <Landing onPlay={onPlay} error={error} />;
}
